import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, ArrowUpRight, Trophy, MapPin } from "lucide-react";

const API = `${BACKEND_URL}/api`;

// ISO 3166-1 alpha-2 → flag emoji
const flag = (cc) => {
  if (!cc || cc.length !== 2) return "🌐";
  const base = 0x1F1E6 - 65;
  return String.fromCodePoint(...cc.toUpperCase().split("").map(c => c.charCodeAt(0) + base));
};

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

/**
 * Partner of the Month — homepage social-proof block.
 *
 * Pulls the #1 partner from /api/public/affiliate-leaderboard?period=month and
 * surfaces them on the homepage as living proof the programme works. Anonymised
 * alias + country flag + 30d commission + CTA to /partners.
 */
export default function PartnerOfTheMonth() {
  const [partner, setPartner] = useState(null);
  const [runnersUp, setRunnersUp] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/affiliate-leaderboard?period=month&limit=4`)
      .then(r => {
        const ps = r.data?.partners || [];
        setPartner(ps[0] || null);
        setRunnersUp(ps.slice(1, 4));
      })
      .catch(() => {});
  }, []);

  if (!partner) return null;

  // Build mini sparkline from partner.spark (14 days)
  const spark = partner.spark || [];
  const sparkW = 320, sparkH = 56, pad = 4;
  const max = Math.max(...spark, 1);
  const points = spark.map((v, i) => {
    const x = pad + (i / Math.max(1, spark.length - 1)) * (sparkW - pad * 2);
    const y = sparkH - pad - (v / max) * (sparkH - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  return (
    <section className="relative overflow-hidden border-y border-slate-200 bg-white" data-testid="partner-of-the-month">
      <div className="absolute inset-0 dot-grid dot-grid-fade opacity-40 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          {/* Left — copy */}
          <div>
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#0055FF] font-semibold mb-4">
              <Trophy className="size-3.5" /> Partner of the month · 30-day rolling
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold text-slate-900 leading-[1.05]">
              Real partners.<br />
              <span className="text-[#0055FF]">Real recurring income.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-[15px] leading-relaxed max-w-xl">
              Every month we anonymise and publish our top earning partner. No spin, no marketing fluff — just commission paid out, sparkline included.
            </p>
            <div className="mt-7 flex items-center gap-3 flex-wrap">
              <Link to="/partners">
                <button className="inline-flex items-center gap-2 rounded-full h-11 px-6 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-medium shadow-md shadow-[#0055FF]/20 transition-colors" data-testid="potm-cta-apply">
                  Become a partner <ArrowUpRight className="size-4" />
                </button>
              </Link>
              <Link to="/leaderboard">
                <button className="inline-flex items-center gap-2 rounded-full h-11 px-6 bg-white hover:bg-slate-50 border border-slate-300 text-slate-900 text-sm font-medium transition-colors" data-testid="potm-cta-leaderboard">
                  See full leaderboard
                </button>
              </Link>
            </div>
          </div>

          {/* Right — winner card */}
          <div className="relative" data-testid="potm-winner-card">
            <div className="relative rounded-2xl bg-[#0F172A] p-7 sm:p-8 overflow-hidden shadow-xl border border-slate-800">
              {/* subtle dotted grid — same texture as the dark CTA footer */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] bg-[size:18px_18px] pointer-events-none" />

              <div className="relative flex items-start gap-4">
                <span className="inline-grid place-items-center size-10 rounded-lg bg-white/10 text-white shrink-0 ring-1 ring-white/15">
                  <Crown className="size-5" strokeWidth={1.4} />
                </span>
                <div className="flex-1">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#0055FF] font-semibold font-mono">#1 · This Month</div>
                  <div className="mt-1 flex items-center gap-3 flex-wrap">
                    <span className="text-3xl">{flag(partner.country)}</span>
                    <div>
                      <div className="text-2xl font-semibold text-white tracking-tight leading-none">{partner.alias}</div>
                      {partner.city && <div className="text-[12px] text-white/60 mt-1 inline-flex items-center gap-1"><MapPin className="size-2.5" /> {partner.city}</div>}
                    </div>
                  </div>
                </div>
              </div>

              {/* commission big number */}
              <div className="relative mt-7">
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-mono">Commission · 30 days</div>
                <div className="mt-1 text-5xl sm:text-6xl font-semibold text-white tracking-tight tabular-nums leading-none">{fmtGBP(partner.commission)}</div>
              </div>

              {/* sparkline — brand blue (matching homepage Ping Tree palette) */}
              {spark.length > 0 && (
                <div className="relative mt-6">
                  <svg width="100%" height={sparkH} viewBox={`0 0 ${sparkW} ${sparkH}`} preserveAspectRatio="none" className="w-full">
                    <defs>
                      <linearGradient id="potm-spark-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0055FF" stopOpacity="0.35" />
                        <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <polyline points={`${points} ${sparkW - pad},${sparkH - pad} ${pad},${sparkH - pad}`} fill="url(#potm-spark-fill)" stroke="none" />
                    <polyline points={points} fill="none" stroke="#3D7BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}

              {/* stats row */}
              <div className="relative mt-5 grid grid-cols-3 gap-3 pt-5 border-t border-white/8">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Signups</div>
                  <div className="mt-1 text-[15px] font-semibold text-white tabular-nums">{partner.signups.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Conv.</div>
                  <div className="mt-1 text-[15px] font-semibold text-white tabular-nums">{partner.conversion_pct}%</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-mono">Clicks</div>
                  <div className="mt-1 text-[15px] font-semibold text-white tabular-nums">{partner.clicks.toLocaleString()}</div>
                </div>
              </div>
            </div>

            {/* Runners-up row */}
            {runnersUp.length > 0 && (
              <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="potm-runners-up">
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-mono">Runners-up</span>
                {runnersUp.map((p, i) => (
                  <div key={p.rank} className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs shadow-sm">
                    <span className="text-slate-400 font-mono">#{p.rank}</span>
                    <span>{flag(p.country)}</span>
                    <span className="text-slate-700 font-medium">{p.alias}</span>
                    <span className="text-slate-400 font-mono tabular-nums">{fmtGBP(p.commission)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
